import { message } from "antd";
import { RcFile } from "antd/lib/upload";

export interface AddCloudinaryTransformationsOptions {
  /**
   * If true, will override existing transformations in the URL.
   * If false (default), will return the URL as-is if transformations already exist.
   */
  override?: boolean;
}

export const ALLOWED_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/apng",
  "image/avif",
  "image/svg+xml",
  "image/webp",
  "image/bmp",
  "image/tiff",
];

export class AssetsUtils {
  static imageValidation(file: RcFile): boolean {
    const isAllowedType = ALLOWED_IMAGE_MIME_TYPES.includes(file.type);

    if (!isAllowedType) {
      message.error("You can only upload image files!");
    }

    const isLt100M = file.size / 1024 / 1024 < 100;

    if (!isLt100M) {
      message.error("Image must be smaller than 100MB!");
    }

    return isAllowedType && isLt100M;
  }

  /**
   * Adds Cloudinary transformations to an image URL if it is a Cloudinary URL.
   * If transformations already exist in the URL, returns the URL as-is unless override is true.
   * @param imageUrl - The image URL to transform
   * @param transformations - The Cloudinary transformations to apply (e.g., "w_800,h_250,c_fill,g_auto")
   * @param options - Optional configuration object
   * @param options.override - If true, will override existing transformations. Defaults to false.
   * @returns The transformed URL if it's a Cloudinary URL, otherwise returns the original URL
   */
  static addCloudinaryTransformations(
    imageUrl: string | undefined | null,
    transformations: string = "c_fill,g_auto",
    options: AddCloudinaryTransformationsOptions = {},
  ): string | undefined {
    if (!imageUrl) {
      return imageUrl ?? undefined;
    }

    const cloudinaryRegex = /^(https?):\/\/res\.cloudinary\.com\/([^/]+)\/image\/upload\/(.+)$/;
    const match = imageUrl.match(cloudinaryRegex);

    if (!match) {
      return imageUrl;
    }

    const [, protocol, cloudName, restOfPath] = match;
    const { override = false } = options;

    // Check if transformations already exist in the URL
    // Cloudinary URLs format: /image/upload/[transformations]/[version]/[public_id]
    // Transformations are typically patterns like w_100,h_100,c_fill etc. before a version (v123) or public_id
    const pathSegments = restOfPath.split("/");
    const firstSegment = pathSegments[0];
    const hasExistingTransformations = firstSegment.includes("_") && !firstSegment.match(/^v\d+$/);

    if (hasExistingTransformations && !override) {
      return imageUrl;
    }

    let pathWithoutTransformations = restOfPath;
    if (hasExistingTransformations && override) {
      pathWithoutTransformations = pathSegments.slice(1).join("/");
    }

    return `${protocol}://res.cloudinary.com/${cloudName}/image/upload/${transformations}/${pathWithoutTransformations}`;
  }
}
